import { minimatch } from 'minimatch';
import type { FetchParameters, HTTPClientOption } from '@/interfaces/service/api.interface';
import { AUTH_ERROR } from '@/constants/error/api.error.constants';
import { NO_TOKEN_APIS, NO_TOKEN_API_PATTERNS } from '@/constants/path.constants';
import { getToken, handleTokenRefresh, setToken } from '@/utils/auth.util';
import { convertQueryParam } from '@/utils/path.util';
import { AUTH_RECOVERY_ELIGIBLE, type AuthRecoveryRequestInit } from '@/lib/fetch-request-context';
import { reportHandledError } from '@/lib/frontend-error-reporter';
import { TOKEN_REFRESH_URL } from '@/libs/constants/url';

const BASE_HEADERS: HeadersInit = [['Content-Type', 'application/json']];

const AUTHENTICATION_ERROR_CODES = new Set([
  AUTH_ERROR.authenticationError,
  AUTH_ERROR.tokenNotFound,
  AUTH_ERROR.accessTokenExpired,
  AUTH_ERROR.invalidToken,
  AUTH_ERROR.refreshTokenExpired,
  '90005',
  '90006',
  '90007',
  '90008',
  '90012',
  '90038'
]);

const isAuthenticationFailure = (httpStatus: number, response: { status?: string | number; code?: string; errors?: { code?: string }[] }) => {
  const errorCodes = [response.code, ...(Array.isArray(response.errors) ? response.errors.map((error) => error.code) : [])];

  return httpStatus === 401 || Number(response.status) === 401 || errorCodes.some((code) => code && AUTHENTICATION_ERROR_CODES.has(code));
};

const applyBaseUrl = (input: FetchParameters[0], baseUrl?: string) => {
  if (!baseUrl) {
    return input;
  }

  if (typeof input === 'object' && 'url' in input) {
    return new URL(input.url, baseUrl);
  }

  return new URL(input, baseUrl);
};

export const isTokenlessPath = (pathname: string) => NO_TOKEN_APIS.includes(pathname) || NO_TOKEN_API_PATTERNS.some((regex) => minimatch(pathname, regex));

/**
 * 이 요청의 최초 401 을 오류 수집에서 빼도 되는가.
 *
 * **이건 약속이다.** 참을 돌려주면 fetch 모니터가 그 401 을 삼키고,
 * 대신 우리가 "재발급으로 복구하거나, 못 하면 직접 보고한다" 를 책임진다.
 * 복구도 못 하고 보고도 안 하면 **장애가 통째로 관측에서 사라진다** —
 * 그래서 아래 `fetchRequest` 의 모든 실패 갈래에 `reportUnrecoveredAuth` 가 붙어 있다.
 *
 * 별도 함수로 뺀 이유: 심볼을 직접 넣는 테스트는 이 조건이 틀려도 통과한다(실측).
 * 조건 자체를 테스트가 직접 호출할 수 있어야 회귀가 잡힌다.
 */
export const isAuthRecoveryEligible = (token: { accessToken?: string; refreshToken?: string } | null, endpointPath: string): boolean =>
  Boolean(token?.accessToken && token.refreshToken && !isTokenlessPath(endpointPath));

/**
 * 억제해 놓고 복구에 실패했을 때 신호를 되살린다.
 *
 * ⚠️ `reason` 은 **고정 문자열만** 쓴다. 게이트웨이가 메시지로 fingerprint 를 만들기 때문에
 * 가변값이 들어가면 같은 사건이 케이스로 쪼개진다. 가변값은 `path` 로 보낸다.
 */
const reportUnrecoveredAuth = (reason: 'response-not-json' | 'refresh-failed', method: string, path: string) => {
  reportHandledError(`auth recovery failed: ${reason}`, { method, path, status: 401 });
};

const baseRequestInterceptor = (urlData: FetchParameters[0], config: FetchParameters[1]) => {
  const pathname = typeof urlData === 'string' ? urlData.split(/[?#]/, 1)[0] : (urlData as URL).pathname;

  if (isTokenlessPath(pathname)) {
    (config?.headers as Headers)?.delete('Authorization');
  } else {
    const token = getToken();

    if (token !== null) {
      (config?.headers as Headers)?.set('Authorization', token.accessToken);
    }
  }

  return config;
};

const baseResposeInterceptor = async (response: Response) => {
  // const baseResposeInterceptor = async (response: Response): Promiseable<T> => {
  try {
    return response.ok ? Promise.resolve(response) : Promise.reject(response);
  } catch (error) {}
};

export function httpClient<T = Response>({ baseUrl, interceptors = {}, ...requestInit }: HTTPClientOption<T> = {}) {
  return async function <R = T extends Response ? Response : T>(urlData: FetchParameters[0], config?: FetchParameters[1]): Promise<R> {
    if (!baseUrl) {
      baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    }

    interceptors = {
      request: baseRequestInterceptor,
      response: baseResposeInterceptor,
      ...interceptors
    };

    const url = applyBaseUrl(urlData, baseUrl);
    const option = { ...requestInit, ...config };

    const interceptorAppliedOption = interceptors.request ? await interceptors.request(url, option) : option;

    try {
      const response = await fetch(url, interceptorAppliedOption);

      if (interceptors.response) {
        return (await interceptors.response(response)) as R;
      }

      return response as R;
    } catch (error) {
      return error as R;
    }
  };
}

export default async function fetchRequest({ endpoint, method, headers, body }: { endpoint: string; method: 'GET' | 'POST' | 'PATCH' | 'DELETE'; headers?: [string, string][]; body?: object }) {
  const request = httpClient();
  const requestHeaders = new Headers(BASE_HEADERS);

  if (headers) {
    headers.forEach((headerConfig) => {
      requestHeaders.set(headerConfig[0], headerConfig[1]);
    });
  }

  try {
    const currentToken = getToken();
    const endpointPath = endpoint.split(/[?#]/, 1)[0];
    // 참이면 모니터가 최초 401 을 삼킨다 — 그 대가로 아래 실패 갈래에서 우리가 보고해야 한다.
    const authRecoveryEligible = isAuthRecoveryEligible(currentToken, endpointPath);
    let fetchOptions: AuthRecoveryRequestInit & { method: string; headers: Headers; body?: string } = {
      method,
      headers: requestHeaders,
      [AUTH_RECOVERY_ELIGIBLE]: authRecoveryEligible
    };

    if ((method === 'GET' || method === 'DELETE') && body !== undefined) {
      endpoint = `${endpoint}${convertQueryParam(body)}`;
    } else {
      fetchOptions = {
        ...fetchOptions,
        body: JSON.stringify(body)
      };
    }

    try {
      const response = await request(endpoint, fetchOptions);

      let jsonResponse;
      try {
        jsonResponse = await response.json();
      } catch (parseError) {
        // 본문이 JSON 이 아니면(예: 401 text/plain) 복구 판정 자체를 못 하고
        // 아래 catch 로 떨어져 조용히 끝난다. 모니터는 이미 401 을 삼킨 뒤다 —
        // 여기서 되살리지 않으면 이 장애는 어디에도 안 남는다.
        if (authRecoveryEligible && response.status === 401) {
          reportUnrecoveredAuth('response-not-json', method, endpointPath);
        }
        throw parseError;
      }

      const isNotAuthenticated = isAuthenticationFailure(response.status, jsonResponse);

      if (isNotAuthenticated && endpoint !== TOKEN_REFRESH_URL) {
        const refreshedToken = await handleTokenRefresh();

        if (refreshedToken?.accessToken) {
          fetchOptions.headers.set('Authorization', refreshedToken.accessToken);
          fetchOptions[AUTH_RECOVERY_ELIGIBLE] = false;
          const retryResponse = await request(endpoint, fetchOptions);
          return retryResponse.json();
        }

        // 재발급 실패. **모니터가 이미 올린 응답은 다시 올리지 않는다** — 같은 응답이 두 번
        // 잡히면 세션당 30건 예산만 축낸다.
        //
        // 모니터가 침묵하는 경우는 정확히 둘뿐이다(`isRecoverableAuthResponse` 참조):
        //  (1) `401` 이면서 우리가 억제 표시를 단 응답 — 우리가 삼키게 만들었으니 우리가 되살린다
        //  (2) HTTP 오류가 아닌 응답(`< 400`) — 본문만 `status: 401` 인 형태다.
        //      모니터는 HTTP 상태만 보므로 오류로 보지 않는다. 사용자는 TOKEN_EXPIRED 로
        //      튕기는데 관측은 0건이었다.
        //
        // ⚠️ 조건에서 `response.status === 401` 을 빼면 안 된다. `isAuthenticationFailure` 는
        //    `500 + 본문 status:401` 이나 `403 + code:90005` 에도 참이 되는데, 그건 모니터가
        //    억제하지 않고 이미 올린 응답이다(억제는 401 에만 걸린다). 빼면 그대로 중복이 된다.
        if ((response.status === 401 && authRecoveryEligible) || response.status < 400) {
          reportUnrecoveredAuth('refresh-failed', method, endpointPath);
        }
      } else {
        const responseToken = jsonResponse.data ?? jsonResponse.result;

        if (responseToken?.accessToken) {
          setToken(responseToken);
        }
      }

      return jsonResponse;
    } catch (error) {
      return {
        success: false,
        message: 'API request failed',
        data: null
      };
    }
  } catch (error) {
    return {
      success: false,
      message: 'API request failed',
      data: null
    };
  }
}
